<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useSidebar } from 'next/polaris/ui/sidebar';
import AssistantSwitcher from 'dashboard/components-next/polaris/pageComponents/switcher/AssistantSwitcher.vue';
import CreateAssistantDialog from 'dashboard/components-next/polaris/pageComponents/assistant/CreateAssistantDialog.vue';
import PortalSwitcher from 'dashboard/components-next/HelpCenter/PortalSwitcher/PortalSwitcher.vue';
import CreatePortalDialog from 'dashboard/components-next/HelpCenter/PortalSwitcher/CreatePortalDialog.vue';
import ContextPicker from './ContextPicker.vue';

// The picker under tier 2's title: which assistant (Polaris AI, with the inboxes it answers in, D-P2) or which
// portal (Knowledge, D-K1). The list is the one the page header used to open. Inside the phone's navigation
// sheet (`inSheet`), the create dialogs are native <dialog> modals: opening one while the sheet's own modal is
// still open would be a modal-inside-a-modal, so this asks the parent to close the sheet first (the same fix
// ContextTools.vue uses for the ✎ compose button) and open a dialog mounted outside the sheet instead.
const props = defineProps({
  area: { type: Object, required: true },
  inSheet: { type: Boolean, default: false },
});

const emit = defineEmits(['createAssistant', 'createPortal']);

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { isMobile } = useSidebar();

const assistants = useMapGetter('polarisAssistants/getRecords');
const assistantsUiFlags = useMapGetter('polarisAssistants/getUIFlags');
const assistantInboxes = useMapGetter('polarisInboxes/getRecords');
const assistantInboxesUiFlags = useMapGetter('polarisInboxes/getUIFlags');
const portals = useMapGetter('portals/allPortals');

const assistantId = computed(() => Number(route.params.assistantId) || null);
const assistant = computed(
  () => assistants.value?.find(item => item.id === assistantId.value) ?? null
);
// `polarisInboxes/getRecords` is a flat list, not scoped per assistant: right after switching assistants it
// still holds the previous one's inboxes until the new fetch resolves. Hide the count while that fetch is in
// flight instead of pairing the new assistant's name with a stale one.
const assistantStatus = computed(() => {
  if (!assistant.value || assistantInboxesUiFlags.value?.fetchingList) {
    return '';
  }
  const count = assistantInboxes.value?.length ?? 0;
  return count
    ? t('POLARIS.ASSISTANT_SWITCHER.CONNECTED_INBOXES', { count })
    : t('POLARIS.ASSISTANT_SWITCHER.NOT_CONNECTED');
});

// Badge do picker (spec §4.0): Live (verde) quando o assistente tem inbox ligada; Setup (âmbar) senão.
// `inboxes_count` vem no payload do assistente, então vale para todos os itens do dropdown também.
const assistantBadge = computed(() => {
  if (!assistant.value) return null;
  const live = (assistant.value.inboxes_count ?? 0) > 0;
  return {
    tone: live ? 'live' : 'setup',
    label: t(
      live
        ? 'POLARIS.ASSISTANT_SWITCHER.BADGE_LIVE'
        : 'POLARIS.ASSISTANT_SWITCHER.BADGE_SETUP'
    ),
  };
});

// `ContextSidebar.vue`'s tier-2 column only hides this component with CSS on a phone (so tier 2's own width
// transition keeps working, Task 4); it stays mounted. Without this guard that hidden desktop instance and the
// mobile sheet's instance (`inSheet`) would both react to the same assistant and fetch the same lists twice.
// Only the instance that is actually shown for the current viewport fetches.
const isHiddenForViewport = computed(() => isMobile.value && !props.inSheet);

let lastRequestedInboxesAssistantId = null;
watch(
  [() => props.area.name, assistantId],
  ([areaName, id]) => {
    if (areaName !== 'polaris' || isHiddenForViewport.value) return;
    if (!assistants.value?.length && !assistantsUiFlags.value?.fetchingList) {
      store.dispatch('polarisAssistants/get');
    }
    if (id && id !== lastRequestedInboxesAssistantId) {
      lastRequestedInboxesAssistantId = id;
      store.dispatch('polarisInboxes/get', { assistantId: id });
      // Pontos de status do menu (Treinar/Testar/Publicar) durante o setup.
      store.dispatch('polarisSetupStatus/fetch', id);
    }
  },
  { immediate: true }
);

const portal = computed(
  () =>
    portals.value?.find(item => item.slug === route.params.portalSlug) ?? null
);

const createAssistantDialogRef = ref(null);
const createPortalDialogRef = ref(null);

const openCreateAssistant = close => {
  close();
  if (props.inSheet) {
    emit('createAssistant');
    return;
  }
  createAssistantDialogRef.value.dialogRef.open();
};
const openCreatePortal = close => {
  close();
  if (props.inSheet) {
    emit('createPortal');
    return;
  }
  createPortalDialogRef.value.dialogRef.open();
};
</script>

<template>
  <template v-if="area.name === 'polaris'">
    <ContextPicker
      v-if="assistant"
      :label="assistant.name"
      :aria-label="$t('POLARIS.ASSISTANT_SWITCHER.PICKER_LABEL')"
      :status="assistantStatus"
      :badge="assistantBadge"
    >
      <template #default="{ close }">
        <AssistantSwitcher
          @close="close"
          @create-assistant="openCreateAssistant(close)"
        />
      </template>
    </ContextPicker>
    <CreateAssistantDialog
      v-if="!inSheet"
      ref="createAssistantDialogRef"
      type="create"
    />
  </template>
  <template v-else-if="area.name === 'knowledge'">
    <ContextPicker
      v-if="portal"
      :label="portal.name"
      :aria-label="$t('HELP_CENTER.PORTAL_SWITCHER.PICKER_LABEL')"
    >
      <template #default="{ close }">
        <PortalSwitcher
          @close="close"
          @create-portal="openCreatePortal(close)"
        />
      </template>
    </ContextPicker>
    <CreatePortalDialog v-if="!inSheet" ref="createPortalDialogRef" />
  </template>
</template>
